import { Box } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-admin';

import type { AdminUserSummary } from '@/admin/adminApi';
import { absoluteInstant, relativeInstant } from '@/admin/operations/failedMessagePresentation';
import { Avatar, Badge, Button, MISSING_VALUE, type DataTableColumn } from '@/ui/kit';

import { accountStates } from './accountPresentation';
import { TechnicalId } from './accountsUi';
import { accountDetailPath } from './routes';

/**
 * Colonnes et pagination **communes** a la liste des comptes (P04) et a la
 * moderation (P11) : les deux montrent le meme tableau, l'une filtree sur les
 * comptes suspendus. Une seule definition, pour qu'un compte se lise de la meme
 * facon des deux cotes.
 */

/** Un compte anonymise n'a plus d'adresse : on dit ce qu'il est, pas l'adresse technique. */
export const accountTitle = (account: AdminUserSummary): string =>
  account.anonymized ? 'Compte anonymisé' : account.email;

/**
 * Adresse (valeur principale, lien vers la fiche) et identifiant copiable au second rang.
 *
 * `search` est la chaine de requete a conserver dans le lien : la liste des
 * comptes range ses filtres dans l'URL, et une fiche ouverte dans son volet ne
 * doit pas les jeter. La moderation n'en passe aucune.
 */
export const AccountIdentity = ({ account, search = '' }: { account: AdminUserSummary; search?: string }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
    <Avatar name={account.anonymized ? '—' : account.email} tone={account.anonymized ? 'yellow' : 'pink'} />
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Link
        to={{ pathname: accountDetailPath(account.id), search }}
        aria-label={`Ouvrir la fiche de ${accountTitle(account)}`}
        sx={{ fontWeight: 700, color: 'tokens.textStrong', overflowWrap: 'anywhere' }}
      >
        {accountTitle(account)}
      </Link>
      <TechnicalId value={account.id} label="l’identifiant du compte" abbreviated />
    </Box>
  </Box>
);

export const AccountStates = ({ account }: { account: AdminUserSummary }): ReactElement => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {accountStates(account).map((state) => (
      <Badge key={state.label} label={state.label} tone={state.tone} dot />
    ))}
  </Box>
);

/**
 * « il y a 4 min », date complète en infobulle ; « — » sans session ouverte.
 *
 * Le tiret est rendu **ici** : `DataTable` ne remplace par « — » qu'une cellule dont
 * le `render` rend `null`, pas un element React qui ne rend rien.
 */
export const LastActivity = ({ iso, now }: { iso: string | null; now: number }): ReactElement => {
  const relative = relativeInstant(iso, now);

  return relative === null ? (
    <Box component="span">{MISSING_VALUE}</Box>
  ) : (
    <Box component="span" title={absoluteInstant(iso) ?? undefined} sx={{ whiteSpace: 'nowrap' }}>
      {relative}
    </Box>
  );
};

/** Une date ne se coupe pas en deux lignes dans une cellule etroite. */
const NoWrap = ({ children }: { children: string | null }): ReactElement | null =>
  children === null ? null : <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{children}</Box>;

const DATE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const day = (iso: string): string | null => {
  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? null : DATE.format(date);
};

/**
 * Colonnes du tableau. `now` est l'instant de la lecture (`LoadedAccounts`) ;
 * `search` la chaine de requete que les liens de fiche conservent ; `compact`
 * ne garde que le compte et son etat (fiche ouverte a cote).
 *
 * Un compte anonymise n'expose ni profils ni appareils : « — », jamais `0`, qui
 * dirait « aucun » d'un compte dont le journal ne s'expose plus.
 */
export const accountColumns = (
  now: number,
  search = '',
  compact = false,
): readonly DataTableColumn<AdminUserSummary>[] => {
  const columns: readonly DataTableColumn<AdminUserSummary>[] = [
    { key: 'account', header: 'Compte', primary: true, render: (account) => <AccountIdentity account={account} search={search} /> },
    { key: 'states', header: 'État', render: (account) => <AccountStates account={account} /> },
    { key: 'babies', header: 'Bébés', render: (account) => (account.anonymized ? null : account.babyCount) },
    { key: 'devices', header: 'Appareils', render: (account) => (account.anonymized ? null : account.activeDeviceCount) },
    { key: 'activity', header: 'Dernière activité', render: (account) => <LastActivity iso={account.lastActivityAt} now={now} /> },
    { key: 'created', header: 'Inscription', render: (account) => <NoWrap>{day(account.createdAt)}</NoWrap> },
  ];

  // Fiche ouverte a cote : le volet prend la moitie de l'ecran, les colonnes de mesure
  // s'effacent plutot que de ramener l'adresse a une lettre par ligne.
  return compact ? columns.slice(0, 2) : columns;
};

export interface PagerState {
  readonly page: number;
  readonly perPage: number;
  readonly itemCount: number;
  /** Total **du serveur** ; jamais deduit. */
  readonly total: number;
}

const NUMBER = new Intl.NumberFormat('fr-FR');

/** « 1–25 sur 1 482 · total exact » ; vide quand la page n'a aucune ligne. */
export const describeRange = ({ page, perPage, itemCount, total }: PagerState): string => {
  if (itemCount === 0) {
    return total === 0 ? 'Aucun résultat' : `Aucune ligne sur la page ${NUMBER.format(page)}`;
  }

  const first = (page - 1) * perPage + 1;

  return `${NUMBER.format(first)}–${NUMBER.format(first + itemCount - 1)} sur ${NUMBER.format(total)} · total exact`;
};

export const AccountsPager = ({
  state,
  busy,
  onPage,
}: {
  state: PagerState;
  busy: boolean;
  onPage: (page: number) => void;
}): ReactElement => (
  <Box
    component="nav"
    aria-label="Pagination"
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
  >
    <Box component="p" role="status" sx={{ m: 0, fontSize: 13, color: 'tokens.textMuted' }}>
      {describeRange(state)}
    </Box>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button size="S" icon={ChevronLeft} disabled={state.page <= 1 || busy} onClick={() => { onPage(state.page - 1); }}>
        Page précédente
      </Button>
      <Button
        size="S"
        icon={ChevronRight}
        disabled={state.page * state.perPage >= state.total || busy}
        onClick={() => { onPage(state.page + 1); }}
      >
        Page suivante
      </Button>
    </Box>
  </Box>
);
